/**
 * Timeline preview: the OGraf action schedule that shows one phase on the canvas (or the whole graphic, "play
 * through"), run by the graphic runtime exactly as goToTime does after setActionsSchedule. The playhead's local
 * time t maps to schedule time `offset + t`, so a frame in the editor is the frame PRISM renders.
 */
import type { Scene } from '@prism-graphics/scene-schema';
import type { ScheduleAction } from '@prism-graphics/runtime/static';
import { getPhase, type PhaseRef } from './timeline.ts';

export type PreviewTarget = PhaseRef | { kind: 'through' };

export interface PreviewSchedule {
  schedule: ScheduleAction[];
  /** Schedule time of local time 0. */
  offset: number;
  /** Length of the preview (ms of local time). */
  duration: number;
  /** Loop phases repeat; the preview wraps when playing. */
  loops: boolean;
  /** Play through: where each phase starts, for markers on the ruler. */
  marks: Array<{ t: number; label: string }>;
}

/** How long play-through holds at each step before the next action. */
export const THROUGH_HOLD = 1000;

/** Data for previewing an update: the test values, or (when they equal the defaults) every string changed. */
export function updatePreviewData(scene: Scene, testData: Record<string, unknown>): Record<string, unknown> {
  const differs = scene.fields.some((f) => f.id in testData && testData[f.id] !== f.default);
  if (differs) return testData;
  const out: Record<string, unknown> = {};
  for (const f of scene.fields) {
    if (f.type === 'string' && !f.enum) out[f.id] = `${f.default} (updated)`.slice(0, f.maxLength ?? Infinity);
    if (f.type === 'number') out[f.id] = Math.min(f.maximum ?? Infinity, f.default + 1);
    if (f.type === 'boolean') out[f.id] = !f.default;
    if (f.type === 'color') out[f.id] = f.default.toUpperCase() === '#FFFFFF' ? '#E94560' : '#FFFFFF';
  }
  return out;
}

const play = (t: number, params: ScheduleAction['params'] = {}): ScheduleAction => ({ t, type: 'playAction', params });

export function previewSchedule(scene: Scene, target: PreviewTarget, testData: Record<string, unknown> = {}): PreviewSchedule {
  const t = scene.timeline;
  const n = t.steps.length;
  const base: PreviewSchedule = { schedule: [], offset: 0, duration: 0, loops: false, marks: [] };
  // Set-up actions (skipped to their end) and the previewed action share time 0; they apply in list order.
  const at = 0;
  switch (target.kind) {
    case 'in':
      return { ...base, schedule: [play(at)], offset: at, duration: t.in.duration };
    case 'step': {
      const from = target.index > 0 ? target.index - 1 : 1;
      return {
        ...base,
        schedule: [play(0, { goto: from, skipAnimation: true }), play(at, { goto: target.index })],
        offset: at,
        duration: getPhase(scene, target)?.duration ?? 0,
      };
    }
    case 'loop':
      return { ...base, schedule: [play(0, { goto: target.index, skipAnimation: true })], offset: 0, duration: t.steps[target.index]?.loop?.duration ?? 0, loops: true };
    case 'out': {
      const s: ScheduleAction[] = [play(0, n > 0 ? { goto: n - 1, skipAnimation: true } : { skipAnimation: true }), { t: at, type: 'stopAction', params: {} }];
      return { ...base, schedule: s, offset: at, duration: t.out.duration };
    }
    case 'update':
    case 'field': {
      const data = updatePreviewData(scene, testData);
      const duration = target.kind === 'update' ? t.update.duration : (getPhase(scene, target)?.duration ?? 0);
      return { ...base, schedule: [play(0, { skipAnimation: true }), { t: at, type: 'updateAction', params: { data } }], offset: at, duration };
    }
    case 'custom': {
      const a = t.customActions[target.index];
      return { ...base, schedule: [play(0, { skipAnimation: true }), { t: at, type: 'customAction', params: { id: a?.id ?? '' } }], offset: at, duration: a?.phase.duration ?? 0 };
    }
    case 'through': {
      const schedule: ScheduleAction[] = [play(at)];
      const marks = [{ t: 0, label: 'In' }];
      if (n === 0) {
        const total = t.in.duration + (t.autoOutAfter ?? 0) + t.out.duration;
        marks.push({ t: t.in.duration + (t.autoOutAfter ?? 0), label: 'Out' });
        return { ...base, schedule, offset: at, duration: total, marks };
      }
      let cursor = t.in.duration + THROUGH_HOLD;
      for (let i = 1; i < n; i++) {
        schedule.push(play(at + cursor));
        marks.push({ t: cursor, label: t.steps[i]!.name || `Step ${i + 1}` });
        cursor += (t.steps[i]!.enter?.duration ?? 0) + THROUGH_HOLD;
      }
      schedule.push({ t: at + cursor, type: 'stopAction', params: {} });
      marks.push({ t: cursor, label: 'Out' });
      return { ...base, schedule, offset: at, duration: cursor + t.out.duration, marks };
    }
  }
}
